/**
 * Form Designer commands — one source of truth for FORM / CARD / CONTROL
 * actions and for what the command bar may enable.
 *
 * The canvas empty-state "+ Add Card" button and the command-bar Add Card
 * button both call `runAddCard`. Add Control and the toolbox Text Box both
 * go through `runAddControl`. Destructive commands never act on the wrong
 * level: Delete Card will not remove a control, and Delete Control will not
 * remove a card.
 */

import {
  addCard,
  addStaticControl,
  emptyForm,
  findCard,
  findControl,
  removeCard,
  removeControl,
} from "@/lib/formDesigner";

export const SELECTION_KIND = Object.freeze({
  FORM: "form",
  CARD: "card",
  CONTROL: "control",
});

export const DEFAULT_ADD_CONTROL_TYPE = "text";

export function selectForm() {
  return { kind: SELECTION_KIND.FORM };
}

export function selectCard(id, ids) {
  const cardId = String(id || "").trim();
  if (!cardId) return selectForm();
  const list = Array.isArray(ids) && ids.length ? ids : [cardId];
  return { kind: SELECTION_KIND.CARD, id: cardId, ids: list };
}

export function selectControl(id) {
  const controlId = String(id || "").trim();
  if (!controlId) return selectForm();
  return { kind: SELECTION_KIND.CONTROL, id: controlId };
}

/**
 * Explicit selection state. Only one primary design object is selected.
 * `selectedForm` is the open document; `selectedCard` / `selectedControl`
 * are set only when that level is the primary selection.
 */
export function selectionState(form, selection) {
  const kind = selection?.kind || SELECTION_KIND.FORM;
  const selectedControl = kind === SELECTION_KIND.CONTROL ? findControl(form, selection.id) : null;
  const selectedCard = kind === SELECTION_KIND.CARD ? findCard(form, selection.id) : null;
  return {
    kind: selectedControl ? SELECTION_KIND.CONTROL : selectedCard ? SELECTION_KIND.CARD : SELECTION_KIND.FORM,
    selectedForm: form || null,
    selectedCard,
    selectedControl,
  };
}

/** A form "exists" once it is more than the untouched blank document. */
export function formIsPresent(form) {
  if (!form) return false;
  if (form.id) return true;
  if (String(form.primaryTable || "").trim()) return true;
  if ((form.cards || []).length > 0) return true;
  if ((form.controls || []).length > 0) return true;
  return String(form.name || "").trim() !== "" && String(form.name).trim() !== emptyForm().name;
}

export function commandAvailability({ form, selection, readOnly = false, dirty = false } = {}) {
  const state = selectionState(form, selection);
  const writable = !readOnly && Boolean(form);
  return {
    canNewForm: writable,
    canDeleteForm: writable && formIsPresent(form),
    canAddCard: writable,
    canDeleteCard: writable && state.kind === SELECTION_KIND.CARD && Boolean(state.selectedCard),
    canAddControl: writable && state.kind === SELECTION_KIND.CARD && Boolean(state.selectedCard),
    canDeleteControl: writable && state.kind === SELECTION_KIND.CONTROL && Boolean(state.selectedControl),
    canSave: writable && Boolean(dirty) && Boolean(form?.primaryTable),
  };
}

export function runAddCard(form, title = "") {
  const result = addCard(form, title);
  if (!result) return null;
  return {
    form: result.form,
    card: result.card,
    selection: selectCard(result.card.id),
  };
}

export function runDeleteCard(form, cardId) {
  if (!findCard(form, cardId)) return null;
  const next = removeCard(form, cardId);
  if (!next) return null;
  return { form: next, selection: selectForm() };
}

export function runAddControl(form, cardId, controlType = DEFAULT_ADD_CONTROL_TYPE) {
  if (!findCard(form, cardId)) return null;
  const result = addStaticControl(form, cardId, controlType);
  if (!result) return null;
  return {
    form: result.form,
    control: result.control,
    selection: selectControl(result.control.id),
  };
}

export function runDeleteControl(form, controlId) {
  const control = findControl(form, controlId);
  if (!control) return null;
  const next = removeControl(form, controlId);
  if (!next) return null;
  const card = findCard(next, control.cardId);
  return {
    form: next,
    selection: card ? selectCard(card.id) : selectForm(),
  };
}

export function runNewForm() {
  return { form: emptyForm(), selection: selectForm() };
}
